import { useLayoutEffect, useRef } from "react";
import { styled, ThemeProvider } from "styled-components";

import {
  CollectionIcon,
  CommunitiesIcon,
  DeleteIcon,
  EditIcon,
  ExpandableText,
  ShareIcon,
  UIButton,
} from "common/components";
import { cssResets } from "common/styles/cssResets";
import { maxWidth } from "common/styles/mediaQueries/breakpoints";
import { extractYoutubeVideoId } from "common/utilities/extractYoutubeVideoId";
import { hardNavigate } from "common/utilities/hardNavigate";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { useTranslate } from "common/utilities/useTranslate";
import type { ScrollPositions } from "context/session/sessionTypes";
import { useSession } from "context/session/useSession";
import { CollectionSearchableTopicList } from "domains/collection/components/CollectionSearchableTopicList";
import { CollectionVideoCover } from "domains/collection/components/CollectionVideoCover";
import type { CollectionTopicsMeta } from "domains/collection/types";
import type { CollectionViewResponse } from "domains/collection/types/collectionResponses";
import { isRtl } from "domains/language/isRtl";
import { getSiteLanguage } from "domains/language/selectors";
import { activateModal } from "domains/modal/actions";
import { shareLinkAction } from "domains/share/actions";
import { darkTheme } from "domains/theme/constants/theme";
import { setVisualMediaPlayer } from "domains/visualMediaPlayer/actions";
import { MediaType } from "domains/visualMediaPlayer/types";
import { getCanEditCollections } from "entities/permissions/selectors";
import type { Topic } from "entities/topics/types";

type CollectionProps = {
  collection: CollectionViewResponse;
  loadNextPage: () => void;
  topics: Topic[];
  topicsMeta: CollectionTopicsMeta;
};

export const CollectionPage = ({ collection, loadNextPage, topics, topicsMeta }: CollectionProps) => {
  const dispatch = useDispatch();
  const go = useGo();
  const canEditCollections = useSelector(getCanEditCollections);
  const siteLanguage = useSelector(getSiteLanguage);
  const translate = useTranslate();
  const { scrollPositions, updateSession } = useSession();

  const scrollPositionsRef = useRef<ScrollPositions | undefined>(scrollPositions);

  useLayoutEffect(() => {
    if (scrollPositionsRef.current?.collectionPage === undefined) return;
    scrollTo(0, scrollPositionsRef.current.collectionPage);
    updateSession((state) => ({ ...state, scrollPositions: { ...state.scrollPositions, collectionPage: undefined } }));
  }, [updateSession]);

  const { co_brands, description, featured_video_url, hero_image_url, id, name, spaces_url: spacesUrl } = collection;

  const videoId = featured_video_url === null ? undefined : extractYoutubeVideoId(featured_video_url);
  const image = videoId === undefined ? undefined : `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  const onClickVideo = () => {
    if (featured_video_url === null) return;
    dispatch(
      setVisualMediaPlayer({
        id: "introduction",
        mediaType: MediaType.videoViaEmbeddedHtmlPlayer,
        name,
        url: featured_video_url,
        isYoutubeVideo: true,
      }),
    );
  };

  const onClickShare = () =>
    dispatch(
      shareLinkAction({
        id,
        image: hero_image_url,
        link: `${import.meta.env.VITE_WEBSITE_URL}/collection/${id}`,
        name,
        type: "Collection",
      }),
    );

  const onRelatedResourceClick = () => {
    if (collection.related_resource === null) return;
    if (collection.related_resource.url === null) return;

    go(collection.related_resource.url);
  };

  const onClickEdit = () => go(`/collection/${id}/edit`);
  const onClickDelete = () => dispatch(activateModal({ id: "CollectionDeletionConfirmation", params: { id, name } }));

  return (
    <Container>
      <Head rtl={isRtl(siteLanguage)}>
        <HeroImage url={hero_image_url} />
        <ThemeProvider theme={darkTheme}>
          <Foreground>
            <Details>
              {co_brands.map((coBrand) => (
                <CoBrand key={coBrand.id}>
                  <CoBrandImage src={coBrand.image_url ?? undefined} />
                  <CoBrandName>{coBrand.name}</CoBrandName>
                </CoBrand>
              ))}
              <Name>{name}</Name>
              <StyledExpandableText text={description} limit={4} lineHeight={28} />
            </Details>
            {image === undefined || (
              <VideoContainer onClick={onClickVideo}>
                <CollectionVideoCover image={image} />
              </VideoContainer>
            )}
          </Foreground>
        </ThemeProvider>
      </Head>
      <CollectionSearchableTopicList
        collection={collection}
        loadNextPage={loadNextPage}
        nextPageMayExist={topicsMeta.nextPageMayExist}
        status={topicsMeta.status}
        topics={topics}
      >
        <Buttons>
          <Button variant="outlined" onClick={onClickShare}>
            <ButtonContentContainer>
              {translate("app.collection.share")}
              <StyledShareIcon />
            </ButtonContentContainer>
          </Button>
          {collection.related_resource?.url && (
            <Button variant="outlined" onClick={onRelatedResourceClick}>
              <ButtonContentContainer>
                {collection.related_resource.title ?? translate("app.collection.related_collection")}
                <StyledCollectionIcon />
              </ButtonContentContainer>
            </Button>
          )}
          {spacesUrl === null || (
            <Button variant="outlined" onClick={() => hardNavigate(spacesUrl)}>
              <ButtonContentContainer>
                {translate("app.collection.community")}
                <StyledCommunitiesIcon />
              </ButtonContentContainer>
            </Button>
          )}
          {canEditCollections && collection.can_edit && (
            <>
              <Button variant="outlined" onClick={onClickEdit}>
                <ButtonContentContainer>
                  {translate("app.collection.edit")}
                  <StyledEditIcon />
                </ButtonContentContainer>
              </Button>
              <Button variant="outlined" onClick={onClickDelete}>
                <ButtonContentContainer>
                  {translate("app.collection.delete")}
                  <StyledDeleteIcon />
                </ButtonContentContainer>
              </Button>
            </>
          )}
        </Buttons>
      </CollectionSearchableTopicList>
    </Container>
  );
};

const Container = styled.div`
  background-color: ${({ theme }) => theme.colors.collectionBackground};
`;

const Head = styled.div<{ rtl: boolean }>`
  background: ${({ theme }) => theme.colors.black};
  direction: ${({ rtl }) => (rtl ? "rtl" : "ltr")};
  min-height: 380px;
  position: relative;
`;

const HeroImage = styled.div<{ url: string }>`
  background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0), #000), url(${({ url }) => url});
  background-position: center;
  background-size: cover;
  height: 380px;
  position: absolute;
  width: 100%;
`;

const Foreground = styled.div`
  display: flex;
  gap: 1rem;
  padding: 6rem 3rem 3rem;

  @media ${maxWidth.medium} {
    flex-direction: column;
    padding: 4rem 1rem 3rem;
  }
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  flex: 10 0 55%;
  gap: 1rem;
  height: 100%;
  justify-content: center;
  max-width: 80vw;
  position: relative;
  width: 100%;
`;

const CoBrand = styled.div`
  align-items: center;
  display: flex;
  gap: 1rem;
`;

const CoBrandImage = styled.img`
  border-radius: 5px;
  height: 54px;
  object-fit: cover;
  object-position: center;
  width: 54px;
`;

const CoBrandName = styled.div`
  color: white;
  font-size: 1.2rem;
  font-weight: bold;
`;

const Name = styled.h1`
  ${cssResets.h1}
  color: white;
  font-size: 2rem;
  font-weight: bold;
`;

const StyledExpandableText = styled(ExpandableText)`
  p {
    color: white;
    font-size: 1.1rem;
    line-height: 28px;
    white-space: pre-line;
  }
`;

const VideoContainer = styled.div`
  align-items: flex-end;
  display: flex;
  flex: 0 0 45%;
  max-width: 40rem;
  width: 100%;
`;

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Button = styled(UIButton)`
  border-radius: 1.5rem;
  padding: 0 1rem 0 1.25rem;

  > div {
    gap: 0.5rem;
  }
`;

const ButtonContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  row-gap: 0.5rem;
`;

const [StyledCommunitiesIcon, StyledDeleteIcon, StyledEditIcon, StyledShareIcon, StyledCollectionIcon] = [
  CommunitiesIcon,
  DeleteIcon,
  EditIcon,
  ShareIcon,
  CollectionIcon,
].map(
  (icon) => styled(icon)`
    height: 14px;
  `,
);
